import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = [
  { key: "all", label: "All" },
  { key: "indoor", label: "Indoor" },
  { key: "outdoor", label: "Outdoor" },
];

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/portfolio`, { params: filter !== "all" ? { category: filter } : {} })
      .then((r) => mounted && setItems(r.data))
      .catch(() => mounted && setItems([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [filter]);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="border-t border-[#E5E0D8] bg-bone">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <p className="overline mb-5">Selected Work</p>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl text-text-main leading-tight">
              Recent installations
              <br />
              <span className="italic text-cedar">across Central Ohio.</span>
            </h2>
          </div>
          <div className="flex items-center gap-2" data-testid="portfolio-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`portfolio-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`font-accent text-xs uppercase tracking-widest px-5 py-3 rounded-full border transition-colors ${
                  filter === f.key
                    ? "bg-text-main text-bone border-text-main"
                    : "bg-transparent text-text-main border-[#E5E0D8] hover:border-cedar hover:text-cedar"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div data-testid="portfolio-loading" className="text-text-muted font-accent text-sm">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8" data-testid="portfolio-grid">
            {items.map((it, i) => {
              const isFull = it.span === "full";
              const isLarge = it.span === "large";
              const colClass = isFull ? "md:col-span-12" : isLarge ? "md:col-span-8" : "md:col-span-4";
              const heightClass = isFull
                ? "h-[420px] md:h-[600px]"
                : isLarge
                ? "h-[420px] md:h-[560px]"
                : "h-[320px] md:h-[420px]";
              return (
                <figure
                  key={it.id}
                  data-testid={`portfolio-item-${it.id}`}
                  className={`group relative overflow-hidden border border-[#E5E0D8] hover-lift ${colClass}`}
                >
                  <img
                    src={it.image}
                    alt={it.title}
                    className={`w-full ${heightClass} object-cover img-zoom`}
                    loading={i < 2 ? "eager" : "lazy"}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  <figcaption className="absolute bottom-6 left-6 right-6 text-white">
                    <div className="overline !text-white/80">{it.category}</div>
                    <div className="font-serif-display text-2xl md:text-3xl mt-2">{it.title}</div>
                    <div className="flex items-center gap-2 mt-3 opacity-90">
                      <MapPin size={14} strokeWidth={1.5} />
                      <span className="font-accent text-xs tracking-wider">{it.location}</span>
                    </div>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
